import AddIcon from "@mui/icons-material/Add";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { Button, Chip, InputAdornment, Link, MenuItem, OutlinedInput, Select, SelectChangeEvent } from "@mui/material";
import {
  getGridStringOperators,
  GridActionsCellItem,
  GridColDef,
  GridFilterItem,
  GridFilterModel,
  GridLogicOperator,
  GridRowParams,
  GridRowsProp,
} from "@mui/x-data-grid-premium";
import { ChangeEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router";
import { debounce } from "throttle-debounce";
import AssetTypeChip from "../../../../../Components/Misc/Chips/AssetChip/asset-type-chip";
import ListPageHeader, {
  ListPageHeaderGroupActions,
  ListPageHeaderRight,
} from "../../../../../Components/Misc/ListPageHeader";
import { ModalContext } from "../../../../../Components/Modals";
import ModalSetFollowers, {
  ModalSetFollowersElementProps,
} from "../../../../../Components/Modals/ModalSetFollowers/modal-set-followers";
import AssetPanel from "../../../../../Components/Panels/AssetPanel/asset-panel";
import DataTable, { CellSize } from "../../../../../Components/Table/table";
import TableCellDateTime from "../../../../../Components/Table/TableCellDateTime/table-cell-date-time";
import TableCellSingleCover from "../../../../../Components/Table/TableCellSingleCover/table-cell-single-cover";
import TableCellStringExpend, {
  TableCellStringExpendDescription,
} from "../../../../../Components/Table/TableCellStringExpend/table-cell-string-expend";
import TableSkeleton from "../../../../../Components/Table/TableSkeleton";
import { assetService, ingestService, searchService } from "../../../../../Core/Api/services";
import { ObjectClass } from "../../../../../Core/Api/Types/object-class";
import { ObjectType } from "../../../../../Core/Api/Types/object-type";
import { routePaths } from "../../../../../Router/router";
import { GetEnumValueLabelMap } from "../../../../../Utils/helpers";
import useCustomSnackbar from "../../../../../Utils/Hooks/useCustomSnackbar";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../../../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import ModalAsperaUpload, {
  ModalAsperaUploadElementProps,
} from "../../../../../Components/Modals/ModalAsperaUpload/modal-aspera-upload";
import ModalNewAssignment, {
  ModalNewAssignmentElementProps,
} from "../../../../../Components/Modals/ModalNewAssignment/modal-new-assignment";
import { ProviderNewAssignmentDto } from "../../../../../Core/Api/Types/db-lab-provider-infos";
import ModalEditMetadata, {
  ModalEditMetadataElementProps,
} from "../../../../../Components/Modals/ModalEditMetadata/modal-edit-metadata";
import { ObjectStatus } from "../../../../../Core/Api/Types/object-status";
import NewRequestPanel from "../../../../../Components/Panels/NewRequestPanels/new-request-panel";
import { GridFilterOperator } from "@mui/x-data-grid-premium";
import GlobalConst from "../../../../../Core/Utils/Constants/globals";
import { useTranslation } from "react-i18next";
export interface IngestApprovedRows {
  id: number;
  createdByUserName: string;
  assetId: number;
  assetName: string;
  assetType: ObjectType;
  createdAt: string;
  providerName: string;
  assetClass: ObjectClass | null;
  message: string | null;
}

export interface IngestApprovedProps {
  rows: IngestApprovedRows[];
  filtersData: {
    type: ObjectType[];
    class: (ObjectClass | null)[];
  };
}

const searchAssetOperator: GridFilterOperator = {
  label: "SearchAssetOperator",
  value: "searchAssetOperator",
  getApplyFilterFn(filterItem, column) {
    if (!filterItem.field || !filterItem.operator || !filterItem.value) return null;

    return (params): boolean => {
      // set consts
      let assetName = params.value as string;
      let assetParentName = params.row["assetParentName"] as string;
      let assetParentTitleName = params.row["assetParentTitleName"] as string;
      let searchValue = filterItem.value as string;
      if (!assetName || !assetParentName || !assetParentTitleName) return false;
      //prepare data for includes
      assetName = assetName.toLowerCase().trim();
      assetParentName = assetParentName.toLowerCase().trim();
      assetParentTitleName = assetParentTitleName.toLowerCase().trim();
      searchValue = searchValue.toLowerCase().trim();

      return (
        assetName.includes(searchValue) ||
        assetParentName.includes(searchValue) ||
        assetParentTitleName.includes(searchValue)
      );
    };
  },
};

function IngestApproved() {
  const { t } = useTranslation();
  // context
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };
  // #region states
  // Top filters
  const [filtersData, setFiltersData] = useState<IngestApprovedProps["filtersData"]>({
    type: [],
    class: [],
  });
  // Type filter
  const [typeFilter, setTypeFilter] = useState("");
  // Class filter
  const [classFilter, setClassFilter] = useState("");
  // Loading
  const [isLoading, setIsLoading] = useState(true);
  // Table filter
  const [tableFilterModel, setTableFilterModel] = useState<GridFilterModel>({ items: [] });
  // table rows
  const [rows, setRows] = useState<GridRowsProp>([]);
  // Asset panel and request panel
  const [openAssetPanel, setOpenAssetPanel] = useState(false);
  const [openNewRequestPanel, setOpenNewRequestPanel] = useState(false);
  // Data for modal
  const [newAssignmentProviders, setNewAssignmentProviders] = useState<ProviderNewAssignmentDto[]>([]);
  // #endregion

  // #region Refs and custom hooks
  const selectedAssetId = useRef<number | undefined>(undefined);
  const enqueueCustomSnackbar = useCustomSnackbar();
  // #endregion

  // #region functions
  // Top filters
  const updateFilterModel = (newFilterItems: GridFilterItem[]) => {
    setTableFilterModel({
      items: newFilterItems,
      quickFilterLogicOperator: GridLogicOperator.And,
      logicOperator: GridLogicOperator.Or,
    });
  };

  const handleChangeTypeSelect = (event: SelectChangeEvent) => {
    const typeFilterValue = event.target.value as string;
    // always remove filter
    let newFilterItems: GridFilterItem[] = tableFilterModel.items.filter((x) => x.field !== "assetType");
    // if userName selected :
    if (typeFilterValue !== "") {
      // add filter on userName
      newFilterItems.push({
        id: 0,
        field: "assetType",
        operator: "equals",
        value: typeFilterValue,
      });
    }
    // set states
    updateFilterModel(newFilterItems);
    setTypeFilter(typeFilterValue);
  };

  const handleChangeClassSelect = (event: SelectChangeEvent) => {
    const classFilterValue = event.target.value as string;
    // always remove filter
    let newFilterItems: GridFilterItem[] = tableFilterModel.items.filter((x) => x.field !== "assetClass");
    // if userName selected :
    if (classFilterValue !== "") {
      // add filter on userName
      newFilterItems.push({
        id: 0,
        field: "assetClass",
        operator: "equals",
        value: classFilterValue,
      });
    }
    // set states
    updateFilterModel(newFilterItems);
    setClassFilter(classFilterValue);
  };

  const handleClearFilters = () => {
    updateFilterModel([]);
  };

  // search input management
  const updateSearchValue = useCallback(
    (newSearchValue: string) => {
      let newFilterItems: GridFilterItem[] = tableFilterModel.items.filter((x) => x.field !== "assetName");
      newFilterItems.push({
        id: 8,
        field: "assetName",
        operator: "searchAssetOperator",
        value: newSearchValue,
      });
      updateFilterModel(newFilterItems);
    },
    [tableFilterModel]
  );

  const debouncedUpdateSearchValue = useMemo(() => debounce(500, updateSearchValue), [updateSearchValue]);

  const handleSearchValueChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newSearchValue = event.target.value;
      debouncedUpdateSearchValue(newSearchValue);
    },
    [debouncedUpdateSearchValue]
  );
  // Row buttons
  const handleSetFollowers = (assetId: number, actualFollowersIds: number[]) => {
    if (!openModal) return;

    openModal<ModalSetFollowersElementProps>(ModalSetFollowers, {
      objectIds: [assetId],
      objectType: "asset",
      currentFollowersIds: actualFollowersIds,
    });
  };
  // New assignment
  const handleNewAssignment = (assetId: number, assetProviderId: number) => {
    if (!openModal) return;
    openModal<ModalNewAssignmentElementProps>(ModalNewAssignment, {
      assetId,
      assetProviderId,
      availableProviders: newAssignmentProviders.length > 0 ? newAssignmentProviders : undefined,
    });
  };

  const handleEditMetadataClick = (assetId: number) => {
    if (!openModal) return;

    assetService.GetObjectById(assetId).then((res) => {
      if (res.success && res.graph) {
        openModal<ModalEditMetadataElementProps>(ModalEditMetadata, { objects: [res.graph] });
      } else enqueueCustomSnackbar(res.message || t("alert:error.assetNotFound"), "error");
    });
  };

  const handleAsperaUploadClick = (
    assetId: number,
    assetParentName: string,
    assetName: string,
    assetType: ObjectType,
    assetAnnexFormatIds: number[],
    assetIsHdr: boolean,
    assetClassification: ObjectClass,
    assetProviderId: number
  ) => {
    if (!openModal) return;
    openModal<ModalAsperaUploadElementProps>(ModalAsperaUpload, {
      assetId,
      assetParentName,
      assetName,
      assetType,
      assetAnnexFormatIds,
      assetIsHdr,
      assetClassification,
      assetProviderId,
    });
  };

  const handleDeleteAsset = (assetId: number, comment?: string) => {
    ingestService.DeleteRequest({ id: assetId, comment: comment || null }).then((response) => {
      if (response.objectId === GlobalConst.ELEVATION_RESPONSE_OBJECT_ID)
        enqueueCustomSnackbar(response.message!, "primary");
      else {
        if (response.success)
          enqueueCustomSnackbar(t("alert:success.deletedAssetSuccessfully", { id: assetId }), "success");
        else enqueueCustomSnackbar(t("alert:error.deletingAsset", { id: assetId, message: response.message }), "error");
      }
    });
  };

  const handleDeleteAssetClick = (assetId: number) => {
    if (!openModal) return;

    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      title: t("modal:title.confirmAssetDeletion"),
      displayComment: true,
      onConfirm: (comment?: string) => handleDeleteAsset(assetId, comment),
    });
  };

  // Asset panel
  const hideAssetPanel = () => {
    setOpenAssetPanel(false);
  };

  const showAssetPanel = (e: GridRowParams) => {
    selectedAssetId.current = e.row["assetId"];
    setOpenAssetPanel(true);
  };

  const hideNewRequestPanel = () => {
    setOpenNewRequestPanel(false);
  };

  const showNewRequestPanel = () => {
    setOpenNewRequestPanel(true);
  };
  // #endregion

  // #region Grid definition
  let _rows: GridRowsProp = [];
  type Row = (typeof _rows)[number];

  const ingestProcessingColumns: GridColDef[] = useMemo(
    () => [
      {
        headerName: "Id",
        field: "assetId",
        flex: CellSize.xsmall,
        minWidth: CellSize.xsmall,
        renderCell: (data: Row) => (
          <Link
            color="primary"
            underline="hover"
            component={RouterLink}
            target="_blank"
            to={`${routePaths.asset.path}/${data.value}`}
          >
            #{data.value}
          </Link>
        ),
      },
      {
        headerName: t("common:title"),
        field: "assetParentTitleId",
        flex: CellSize.xsmall,
        minWidth: CellSize.xsmall,
        renderCell: (data: Row) => (
          <TableCellSingleCover coverId={data.value} titleName={data.row["assetParentTitleName"]} />
        ),
      },
      {
        headerName: t("common:type"),
        field: "assetType",
        flex: CellSize.large,
        minWidth: CellSize.large,
        valueOptions: GetEnumValueLabelMap(ObjectType),
        renderCell: (data: Row) => <AssetTypeChip type={data.value} uncolored />,
      },
      {
        headerName: t("common:class"),
        field: "assetClass",
        flex: CellSize.medium,
        minWidth: CellSize.medium,
        renderCell: (data: Row) => <Chip label={data.value} color="default" />,
      },
      {
        headerName: t("common:createdAt"),
        field: "createdAt",
        flex: CellSize.small,
        minWidth: CellSize.small,
        renderCell: (data: Row) => <TableCellDateTime data={data} />,
      },
      {
        headerName: t("table:headerName.technicalSpec"),
        field: "assetName",
        flex: CellSize.xlarge,
        minWidth: CellSize.xlarge,
        filterOperators: [searchAssetOperator].concat(getGridStringOperators()),
        renderCell: (data: Row) => (
          <TableCellStringExpendDescription
            title={data.row["assetParentName"]}
            value={data.value}
            width={data.colDef.computedWidth}
          />
        ),
      },
      {
        headerName: t("common:message"),
        field: "message",
        flex: CellSize.xlarge,
        minWidth: CellSize.xlarge,
        renderCell: (data: Row) => <TableCellStringExpend data={data} />,
      },
      {
        headerName: "",
        field: "actions",
        type: "actions",
        flex: CellSize.actionsSmall,
        minWidth: CellSize.actionsSmall,
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem
            icon={<GroupsOutlinedIcon />}
            label={t("common:setFollowers")}
            onClick={() => handleSetFollowers(params.row["assetId"], params.row["followersIds"])}
            showInMenu
          />,
          <GridActionsCellItem
            icon={<PersonAddIcon />}
            label={t("common:newAssignment")}
            onClick={() => handleNewAssignment(params.row["assetId"], params.row["assetProviderId"])}
            showInMenu
          />,
          <GridActionsCellItem
            icon={<EditOutlinedIcon />}
            label={t("common:editMetadata")}
            onClick={() => handleEditMetadataClick(params.row["assetId"])}
            showInMenu
          />,
          <GridActionsCellItem
            icon={<FileUploadOutlinedIcon />}
            label={t("common:upload")}
            onClick={() =>
              handleAsperaUploadClick(
                params.row["assetId"],
                params.row["assetParentName"],
                params.row["assetName"],
                params.row["assetType"],
                params.row["assetAnnexFormatIds"],
                params.row["assetIsHdr"],
                params.row["assetClass"],
                params.row["assetProviderId"]
              )
            }
            showInMenu
          />,
          <GridActionsCellItem
            icon={<DeleteOutlinedIcon color="error" />}
            label={t("common:delete")}
            color="error"
            onClick={() => handleDeleteAssetClick(params.row["assetId"])}
            showInMenu
          />,
        ],
      },
    ],
    [newAssignmentProviders]
  );
  // #endregion

  // #region hooks
  useEffect(() => {
    searchService.SearchBasicNativeByStatusesIngest([ObjectStatus.Accepted]).then((response) => {
      if (response) {
        setRows(response.rows);
        setFiltersData(response.filtersData);
      }
      setIsLoading(false);
    });
  }, []);
  // #endregion

  return (
    <>
      <ListPageHeader mb={3}>
        <ListPageHeaderGroupActions>
          <Select
            displayEmpty
            IconComponent={ExpandMoreRoundedIcon}
            startAdornment={<PersonOutlineOutlinedIcon />}
            inputProps={{ "aria-label": "Without label" }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            value={typeFilter}
            onChange={handleChangeTypeSelect}
          >
            <MenuItem value="" selected>
              {t("common:type")}
            </MenuItem>
            {filtersData.type.length > 0 &&
              filtersData.type.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
          </Select>
          <Select
            displayEmpty
            IconComponent={ExpandMoreRoundedIcon}
            startAdornment={<ManageAccountsOutlinedIcon />}
            inputProps={{ "aria-label": "Without label" }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            value={classFilter}
            onChange={handleChangeClassSelect}
          >
            <MenuItem value="" selected>
              {t("common:class")}
            </MenuItem>
            {filtersData.class.length > 0 &&
              filtersData.class.map((x) =>
                x ? (
                  <MenuItem key={x} value={x}>
                    {x}
                  </MenuItem>
                ) : (
                  ""
                )
              )}
          </Select>
          {/* Disabled cause not useful
          TODO : maybe add a better filter ? 
          <Select
            displayEmpty
            IconComponent={ExpandMoreRoundedIcon}
            startAdornment={<OutlinedFlagOutlinedIcon />}
            inputProps={{ "aria-label": "Without label" }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            value={priorityFilter}
            onChange={handleChangePrioritySelect}
          >
            <MenuItem value="">Priority level</MenuItem>
            <MenuItem value="High">High</MenuItem>
            <MenuItem value="Normal">Normal</MenuItem>
            <MenuItem value="Low">Low</MenuItem>
          </Select> */}
          {tableFilterModel.items.length > 0 && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<RestartAltOutlinedIcon />}
              sx={{ ml: 1 }}
              onClick={handleClearFilters}
            >
              {t("button:clearAllFilters")}
            </Button>
          )}
        </ListPageHeaderGroupActions>
        <ListPageHeaderRight>
          <OutlinedInput
            placeholder={t("common:search")}
            endAdornment={
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            }
            onChange={handleSearchValueChange}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            sx={{ ml: 1 }}
            onClick={showNewRequestPanel}
          >
            {t("button:newRequest")}
          </Button>
        </ListPageHeaderRight>
      </ListPageHeader>
      {isLoading ? (
        <TableSkeleton />
      ) : (
        <DataTable
          columns={ingestProcessingColumns}
          rows={rows}
          autoHeight
          rowHeight={64}
          pagination={true}
          initialState={{
            sorting: {
              sortModel: [{ field: "createdAt", sort: "desc" }],
            },
          }}
          filterModel={tableFilterModel}
          onRowClick={showAssetPanel}
        />
      )}
      <AssetPanel
        open={openAssetPanel}
        onClose={hideAssetPanel}
        closePanel={hideAssetPanel}
        assetId={selectedAssetId.current}
      />
      <NewRequestPanel type="create" open={openNewRequestPanel} onClose={hideNewRequestPanel} />
    </>
  );
}

export default IngestApproved;
